import { useEffect, useState } from 'react';
import { markPaywallSeen } from '@/lib/analytics/track';
import { ChevronDown, Loader2, Sparkles } from 'lucide-react';
import { cn, MathText } from '@jeraisonne/ui';
import {
  formatEuros,
  offerSavingsCents,
  paywallOffers,
  type SkillcardQuote,
} from '@jeraisonne/db/cards';
import { useAuth } from '@/lib/auth';
import { isNativeApp } from '@/lib/platform';
import {
  skillcardBuyPath,
  skillcardCheckoutErrorMessage,
  startSkillcardCheckout,
  useSkillcardOffers,
} from '@/lib/skillcards/purchase';
import { ExercisePaywallCard } from '@/components/exercise-paywall-card';
import { rememberExerciseExit } from '@/lib/exercise-exit';

/**
 * Paywall d'un exercice relié à une SKILLCARD (migration 0304), affiché à la fin
 * de la zone gratuite (2 questions d'un exercice non découpé) ou, pour une
 * partie payante d'un exercice découpé (migration 0501), à la place de
 * l'écran d'accueil.
 *
 * Offres : la carte, le pack le moins cher qui la contient et le programme
 * en vigueur relié (`paywallOffers`), avec le prix carte par carte barré et
 * l'économie réalisée ; achat direct (Stripe), retour sur l'exercice. En dessous, repliée : l'abonnement / le pass (qui incluent toutes
 * les SKILLCARDS) — c'est le paywall historique, inchangé.
 *
 * Dans le player, affiché SOUS le bilan : les questions gratuites forment un
 * exercice terminé (XP et HORA crédités), d'où `attemptId=null`. Un attemptId
 * fourni est mémorisé pour une reprise après `?pass_success=1`.
 *
 * ⚠️ Natif : pas d'achat de carte (App Store 3.1.1) → paywall historique seul
 * (IAP abonnement sur iOS, Reader app sur Android).
 */
export function SkillcardPaywallCard({
  cardId,
  freeSample,
  freeQuestions,
  exerciseId,
  attemptId,
  onUnlocked,
}: {
  cardId: string;
  freeSample: boolean;
  freeQuestions: number;
  exerciseId: string;
  attemptId: string | null;
  onUnlocked?: () => void;
}) {
  const { session } = useAuth();
  const offers = useSkillcardOffers(cardId, session?.user?.id ?? null);
  const [showPasses, setShowPasses] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Tunnel de conversion « contenu bloqué → achat » (0718).
  useEffect(() => markPaywallSeen('skillcard'), []);

  if (isNativeApp()) {
    return (
      <ExercisePaywallCard
        chapterType="normal"
        exerciseId={exerciseId}
        attemptId={attemptId}
        onUnlocked={onUnlocked}
      />
    );
  }

  const card = offers.data?.card;
  const list = offers.data ? paywallOffers(offers.data) : [];
  const best = list.reduce<SkillcardQuote | null>(
    (acc, q) => (q.kind !== 'card' && (!acc || q.savings_pct > acc.savings_pct) ? q : acc),
    null,
  );

  function rememberAttempt() {
    if (attemptId) sessionStorage.setItem(`exercise-attempt-${exerciseId}`, attemptId);
    // Retour de l'exercice après paiement : page d'origine, sinon la carte.
    rememberExerciseExit(exerciseId, `/card/${cardId}`);
  }

  function goBuyPage() {
    rememberAttempt();
    window.location.href = skillcardBuyPath(cardId, window.location.pathname);
  }

  async function buy(q: SkillcardQuote) {
    setBusy(q.target_id);
    setError(null);
    rememberAttempt();
    try {
      window.location.href = await startSkillcardCheckout({
        kind: q.kind,
        targetId: q.target_id,
        cardId,
        returnPath: window.location.pathname,
      });
    } catch (e) {
      setError(skillcardCheckoutErrorMessage(e instanceof Error ? e.message : 'unknown'));
      setBusy(null);
    }
  }

  return (
    <div className="space-y-3">
      <div className="border-primary-200 from-primary-50 to-ink-50 space-y-4 rounded-2xl border-2 bg-gradient-to-b p-5">
        <div className="space-y-1 text-center">
          <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
            {freeSample
              ? `Tu as fait les ${freeQuestions} questions gratuites 🎉`
              : 'Exercice réservé aux SKILLCARDS débloquées'}
          </p>
          <h3 className="font-display text-ink-900 text-xl font-extrabold">
            Débloque la SKILLCARD
          </h3>
          {card && (
            <p className="text-ink-700 font-sans text-sm font-semibold">
              <MathText>{card.label}</MathText>
            </p>
          )}
          <p className="text-ink-500 font-sans text-sm leading-relaxed">
            Toutes ses questions et tous ses exercices, pendant 1 an.
            {attemptId ? ' Tu reprends exactement où tu en es.' : ''}
          </p>
        </div>

        {offers.isLoading && <div className="bg-ink-100 h-16 animate-pulse rounded-xl" />}

        {list.length > 0 ? (
          <ul className="space-y-2" aria-label="Offres SKILLCARDS">
            {list.map((q) => (
              <li key={`${q.kind}-${q.target_id}`}>
                <PaywallOffer
                  quote={q}
                  best={best?.target_id === q.target_id}
                  busy={busy === q.target_id}
                  disabled={busy !== null}
                  onBuy={() => void buy(q)}
                />
              </li>
            ))}
          </ul>
        ) : (
          !offers.isLoading && (
            <button
              type="button"
              onClick={goBuyPage}
              className="bg-primary-500 font-display hover:bg-primary-600 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white shadow transition-all active:scale-[.98]"
            >
              <Sparkles className="h-4 w-4" aria-hidden />
              Voir les offres
            </button>
          )
        )}
        {error && (
          <p role="alert" className="text-error-600 text-center text-sm">
            {error}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => setShowPasses((v) => !v)}
        aria-expanded={showPasses}
        className="text-ink-600 hover:text-ink-800 mx-auto flex min-h-11 items-center gap-1.5 font-sans text-sm font-semibold"
      >
        Ou tout JeRaisonne, SKILLCARDS comprises
        <ChevronDown
          className={`h-4 w-4 transition-transform ${showPasses ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {showPasses && (
        <ExercisePaywallCard
          chapterType="normal"
          exerciseId={exerciseId}
          attemptId={attemptId}
          onUnlocked={onUnlocked}
        />
      )}
    </div>
  );
}

const KIND_LABELS: Record<SkillcardQuote['kind'], string> = {
  card: 'Cette carte',
  pack: 'Le pack',
  programme: 'Tout le programme',
};

function PaywallOffer({
  quote,
  best,
  busy,
  disabled,
  onBuy,
}: {
  quote: SkillcardQuote;
  best: boolean;
  busy: boolean;
  disabled: boolean;
  onBuy: () => void;
}) {
  const savings = offerSavingsCents(quote);
  const n = quote.cards_to_buy;
  return (
    <button
      type="button"
      onClick={onBuy}
      disabled={disabled}
      className={cn(
        'flex min-h-11 w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition-all active:scale-[.98] disabled:opacity-60',
        best
          ? 'border-primary-400 bg-primary-50 ring-primary-100 ring-2'
          : 'border-ink-200 hover:border-primary-300 bg-white',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="text-primary-600 block font-sans text-[11px] font-semibold uppercase tracking-wider">
          {KIND_LABELS[quote.kind]}
          {best && ' · meilleure affaire'}
        </span>
        <span className="text-ink-900 font-display block truncate text-sm font-bold">
          <MathText>{quote.label}</MathText>
        </span>
        <span className="text-ink-500 block font-sans text-xs">
          {n} carte{n > 1 ? 's' : ''}
          {savings > 0 && (
            <span className="bg-success-50 text-success-700 ml-2 rounded-full px-2 py-0.5 font-semibold">
              −{quote.savings_pct} % · {formatEuros(savings)} économisés
            </span>
          )}
        </span>
      </span>
      <span className="shrink-0 text-right">
        {savings > 0 && (
          <span className="text-ink-400 block font-sans text-xs line-through">
            {formatEuros(quote.unit_total_cents)}
          </span>
        )}
        <span className="text-ink-900 font-display inline-flex items-center gap-1 text-base font-extrabold">
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          {formatEuros(quote.price_cents)}
        </span>
      </span>
    </button>
  );
}
